/**
 * Coups de pouce — Attendu 1116 « Reconnaître la base d'un prisme donné en
 * perspective cavalière » (automatisme 4e).
 *
 * forme_*, retrouver_*, vf_* : entrée PARTAGÉE « la base » — les
 * bases sont les deux faces superposables, pas forcément celle du dessous ;
 * les faces latérales sont des rectangles ; la base a autant de côtés que le
 * prisme a de faces latérales. L'exemple : un prisme couché (bases
 * triangulaires devant et derrière) et un prisme debout (bases pentagonales
 * en haut et en bas), dessinés avec les moteurs du gen.
 *
 * (compter_saisie et rappel_tableau_vf retirés du gen, décision prof 2026-09-11.)
 */
import { prismeTri } from '../maths/5eme/espace/svgSolides';
import { prismePoly, planche } from '../maths/4eme/espace/svgSolides4e';

const PLANCHE = planche([
    { label: 'A', type: 'tri_couche', svg: prismeTri({ salt: 21 }).svg },
    { label: 'B', type: 'penta', svg: prismePoly(5, 22) },
]).replace(/^<svg [^>]*>/, '').replace(/<\/svg>\s*$/, '');

const T = (x, y, s, couleur, poids = 700, taille = 11) => `<text x="${x}" y="${y}" font-size="${taille}" font-weight="${poids}" fill="${couleur}">${s}</text>`;

const SVG_BASE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 330" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Question : quelle est la base de chacun de ces deux prismes droits ? En 3 étapes à faire défiler avec les flèches. Étape 1 : on cherche les deux faces superposables, reliées par des rectangles : ce sont les bases. Étape 2 : le prisme A est posé sur une face latérale, ses bases sont les deux triangles, devant et derrière ; la face du dessous est un rectangle, pas une base. Étape 3 : le prisme B est debout, ses bases sont les deux pentagones, en haut et en bas ; il a 5 faces latérales, autant que de côtés à sa base. Réponse : base de A, un triangle ; base de B, un pentagone">
  <style>@keyframes cp1116-pop { from { opacity: 0; } to { opacity: 1; } } .cp1116-pop { opacity: 0; animation: cp1116-pop 0.5s ease forwards; }</style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Cherche les deux faces superposables</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Prisme couché : les bases sont devant et derrière</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Prisme debout : les bases sont en haut et en bas</text>
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${T(8, 44, 'Question : quelle est la base de chacun de ces prismes droits ?', '#1E3A8A')}
    <g transform="translate(30,76)">${PLANCHE}</g>
    ${T(8, 262, 'Les bases : les deux faces superposables, reliées par des rectangles.', '#1E3A8A')}
  </g>
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1116-pop">
    <rect x="40" y="98" width="130" height="144" rx="8" fill="none" stroke="#1D9E75" stroke-width="2.5" stroke-dasharray="7 4"/>
    ${T(8, 280, 'A est couché : ses bases sont les deux triangles, devant et derrière.', '#1D9E75')}
    ${T(8, 294, 'La face du dessous est un rectangle, pas une base.', '#1D9E75')}
  </g></g>
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1116-pop">
    <rect x="190" y="98" width="130" height="144" rx="8" fill="none" stroke="#B45309" stroke-width="2.5" stroke-dasharray="7 4"/>
    ${T(8, 312, 'B est debout : ses bases sont les pentagones (5 côtés, 5 faces).', '#B45309')}
    ${T(8, 326, 'Réponse : base de A, un triangle ; base de B, un pentagone.', '#1e293b', 700, 11.5)}
  </g></g>
</svg>`.trim();

const AIDE_BASE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Les bases sont les deux faces superposables** d'un prisme droit, reliées entre elles par des rectangles (les faces latérales). Elles ont la même forme et la même taille.",
                "**Ce n'est pas forcément la face du dessous** : un prisme posé sur une face latérale a ses bases devant et derrière. Cherche la forme qui apparaît deux fois, pas celle qui touche le sol.",
                "**Compte les côtés de la base** : le prisme a autant de faces latérales que sa base a de côtés. Base triangle, 3 faces latérales ; base pentagone, 5.",
                "**Pavé et cube** : toutes les faces sont des rectangles (ou des carrés), n'importe quelle paire de faces opposées peut servir de bases.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_BASE },
    ],
};

const CP_1116 = {
    forme_qcm_unique: AIDE_BASE,
    retrouver_qcm_unique: AIDE_BASE,
    vf_tableau_vf: AIDE_BASE,
};

export default CP_1116;
